import React, { useEffect, useRef, useState } from 'react';
import AppInput from '../../../components/atoms/AppInput/AppInput';
import AppButton from '../../../components/atoms/AppButton/AppButton';
import { RuleFormOption } from './RuleForm.RuleFormOption';
import { CriteriaCreate, CriteriaList } from '../../../interfaces/rule';
import { isGeolocation, isServiceCode, isStates, isTown, isTownship } from '../../../utils/Rules/CriteriaValidator';
import { RangeCriteriaFormState } from './RuleForm.RangeCriteriaForm';
import { MeasurementsFormState } from './RuleForm.MeasurementCriteriaForm';
import { GeolocationFormState } from './RuleForm.GeolocationCriteriaForm';
import { AppSelectItemProp } from '../../../components/molecules/AppMultiselect/AppMultiselect';
import { useRuleAdapter } from '../../../hooks/Rules/useRuleAdapter';
import IconAdd from '../../../assets/plus.png';
import { tracking } from '../../../config';

interface Criterias {
  [key: string]: {
    criteriaKey: string;
    criteriaId: number;
    value: RangeCriteriaFormState | MeasurementsFormState | GeolocationFormState | undefined | string | boolean | AppSelectItemProp[]
  } | undefined;
}

export interface RuleFormFormState {
  name: string | undefined,
  criterias: Criterias
}

interface RuleFormProps {
  children?: React.ReactNode;
  criterias: CriteriaList[];
  isLoading?: boolean;
  submitTitleButton: string;
  loadGeolocation: () => void;
  loadServiceCode: (criteriaKey: string) => void;
  loadCities: (criteriaKey: string, countryCode: string, level: number) => void;
  onSubmit: (form: CriteriaCreate) => void;
  onCancel: () => void;
  initialState?: RuleFormFormState;
}

export const RuleForm = ({
                           criterias,
                           submitTitleButton,
                           loadGeolocation,
                           loadServiceCode,
                           loadCities,
                           initialState,
                           isLoading,
                           onSubmit,
                           onCancel,
                         }: RuleFormProps) => {
  const { parseFormToAPI, getAllErrors } = useRuleAdapter();
  const [hasErrors, setHasErrors] = useState({});
  const prevStateForm = useRef(JSON.stringify(initialState));
  const [formState, setFormState] = useState<RuleFormFormState>(initialState || {
    'name': undefined,
    'criterias': {
      [Date.now() + '']: undefined,
    },
  });
  const [disabled, setDisabled] = useState(true);
  const [isOptionLoading, setIsOptionLoading] = useState(false);

  const pushNewCriteria = () => setFormState({
    ...formState,
    criterias: { ...formState.criterias, [Date.now() + '']: undefined },
  });

  const updateCriteriaValue = (key: string, criteria: {
    criteriaKey: string;
    criteriaId: number;
    value: RangeCriteriaFormState | MeasurementsFormState | GeolocationFormState | undefined | string | boolean | AppSelectItemProp[],
  }) => {
    const stateCriteria = { ...formState.criterias };
    stateCriteria[key] = criteria;
    setFormState({ ...formState, criterias: stateCriteria });
  };

  const removeCriteriaByKey = (criteriaKey: string) => {
    const stateCriteria = { ...formState.criterias };
    delete stateCriteria[criteriaKey];
    setFormState({ ...formState, criterias: stateCriteria });
  };

  const validateArray = (formToValid: CriteriaCreate) => {
    if (formState.name === '' || formState.name === undefined) return false;

    const valid = Object.values(formToValid.criterias).filter((obj) => {
      let hasEmptyData = false;
      let hasEmptyValues = false;

      if (obj.data) {
        hasEmptyData = obj.data.some(
          (dataObj) =>
            !(
              dataObj.first_value === '' &&
              dataObj.second_value === ''
            ),
        );
      } else {
        if (obj.second_value)
          hasEmptyValues = !(
            obj.first_value === '' && obj.second_value === ''
          );
        else hasEmptyValues = !(obj.first_value === '');
      }
      return hasEmptyData || hasEmptyValues;
    });

    const filterError = valid
      .map((obj) => obj.error?.filter((e) => e !== ''))
      .flat();

    if (filterError.includes(undefined)) {
      if (!filterError || (filterError.length === 0)) return true;
      return !filterError.some(item => typeof item === 'string');
    } else {
      return (
        Object.values(formState.criterias).length === valid.length &&
        filterError.length === 0
      );
    }
  };

  useEffect(() => {
    const valid = parseFormToAPI(formState);
    setHasErrors(getAllErrors(valid));
    setDisabled(validateArray(valid) && prevStateForm.current !== JSON.stringify(formState) && !Object.values(formState.criterias).includes(undefined));
  }, [formState]);


  return (
    <form onSubmit={e => {
      e.preventDefault();
      onSubmit(parseFormToAPI(formState));
    }}>
      <div className="px-8">
        <div className={'mb-6'}>
          <h2 className={'text-xl mb-4'}>Nombre de la regla</h2>
          <AppInput
            aria-label={'ruletitle'}
            value={formState.name}
            onChange={(event) => setFormState({ ...formState, name: event.target.value })} />
          {
            formState.name === '' && <div className="text-red-500 text-sm mb-5 absolute">
              Este campo es requerido
            </div>
          }
        </div>

        <div className={'mb-6'}>
          <h2 className={'text-xl mb-4'}>Criterios</h2>
          {Object.keys(formState.criterias).map((key) => <div key={key} className={'mb-4'}>
            <RuleFormOption
              criterias={criterias}
              value={formState.criterias[key]}
              disabledOptions={Object.values(formState.criterias).map(c => '' + c?.criteriaKey)}
              isLoading={isOptionLoading}
              onDelete={Object.keys(formState.criterias).length > 1 ? () => {
                removeCriteriaByKey(key);
              } : undefined}
              hasErrors={hasErrors}
              onChange={({ criteriaKey, value, criteriaId }) => {
                if (criteriaKey) {
                  if (isGeolocation(criteriaKey)) {
                    setIsOptionLoading(true);
                    loadGeolocation();
                    if (tracking.countryCode) {
                      if (isStates(criteriaKey)) {
                        setIsOptionLoading(true);
                        loadCities(criteriaKey, tracking.countryCode, 1);
                      }

                      if (isTown(criteriaKey)) {
                        setIsOptionLoading(true);
                        loadCities(criteriaKey, tracking.countryCode, 2);
                      }

                      if (isTownship(criteriaKey)) {
                        setIsOptionLoading(true);
                        loadCities(criteriaKey, tracking.countryCode, 3);
                      }
                    }

                  }

                  if (isServiceCode(criteriaKey)) {
                    setIsOptionLoading(true);
                    loadServiceCode(criteriaKey);
                  }
                  updateCriteriaValue(key, { criteriaKey, value, criteriaId });
                }

              }}
            />
          </div>)}
        </div>
        {Object.keys(formState.criterias).length !== Object.keys(criterias).length &&
          <AppButton
            title="Agregar"
            onClick={pushNewCriteria}
            state="ghost"
            size="md"
            className="font-normal"
            icon={
              <img src={IconAdd} alt="add" className="w-4 h-4" />
            }
          />}


      </div>
      <hr className="my-4" />
      <div className="px-8">
        <div className={'flex gap-3 justify-end'}>
          <AppButton state={'default'} title={'Descartar'} onClick={onCancel} />
          <AppButton title={submitTitleButton} disabled={(isLoading === true) ? true : !disabled} type={'submit'} />
        </div>
      </div>


    </form>
  );
};

RuleForm.displayName = 'RuleForm';

